import { Link } from '@tanstack/react-router';

import { cn } from '../../../shared/lib/cn';
import { Kbd } from '../../../shared/ui/kbd';
import {
  SearchIcon,
  SettingsIcon,
  SidebarCollapseIcon,
  SidebarExpandIcon,
} from '../../../shared/ui/icons';
import { ShortcutTooltip } from '../../../shared/ui/tooltip';
import type { TNotificationDigestState } from '../../../features/notification-feed';
import { NAV_ENTRIES, RAIL_ITEM, RAIL_ITEM_ACTIVE, SHELL_CHORDS } from './nav';
import { RailBadge } from './rail-badge';
import { NotificationsBell } from './notifications-bell';
import { OrgSwitcher, type IOrganization } from './org-switcher';
import { UserMenu, type IUserMenuProps } from './user-menu';

export type TShellMenu = 'organizations' | 'account' | 'notifications' | null;

export interface ISidebarProps {
  current: IOrganization;
  organizations: readonly IOrganization[];
  onSwitch: (id: string) => void;
  onCreateOrganization: () => void;
  onSearch: () => void;
  // `undefined` is no count, not zero: a count nobody has computed must not be drawn as one.
  issueCount: number | undefined;
  notifications: TNotificationDigestState;
  timeZone: string;
  user: IUserMenuProps;
  menu: TShellMenu;
  onMenu: (menu: TShellMenu) => void;
  collapsed: boolean;
  onToggle: () => void;
}

const toggle =
  (menu: Exclude<TShellMenu, null>, onMenu: ISidebarProps['onMenu']) => (open: boolean) =>
    onMenu(open ? menu : null);

// Board 27's rail, grown into a labelled sidebar that collapses back to it. Each item says
// what it is and its key on hover and focus alike, because an icon alone is a guess.
export const Sidebar = ({
  current,
  organizations,
  onSwitch,
  onCreateOrganization,
  onSearch,
  issueCount,
  notifications,
  timeZone,
  user,
  menu,
  onMenu,
  collapsed,
  onToggle,
}: ISidebarProps) => (
  <nav
    aria-label="Main"
    className={cn(
      'flex shrink-0 flex-col gap-1 overflow-x-hidden overflow-y-auto border-r border-border-sunken bg-surface-sunken py-3 transition-[width] duration-200',
      collapsed ? 'w-rail' : 'w-sidebar',
    )}
  >
    <div className="mb-inset flex flex-col">
      <OrgSwitcher
        current={current}
        organizations={organizations}
        open={menu === 'organizations'}
        onOpenChange={toggle('organizations', onMenu)}
        onSwitch={onSwitch}
        onCreate={onCreateOrganization}
        collapsed={collapsed}
      />
    </div>

    <ShortcutTooltip off={!collapsed} label="Search" keys={SHELL_CHORDS.search.keys}>
      <button type="button" aria-label="Search" onClick={onSearch} className={RAIL_ITEM}>
        <SearchIcon size="lg" />
        {!collapsed && (
          <>
            <span className="truncate">Search</span>
            <Kbd className="ml-auto">{SHELL_CHORDS.search.keys}</Kbd>
          </>
        )}
      </button>
    </ShortcutTooltip>

    {NAV_ENTRIES.map(({ to, label, Icon, keys }) => {
      const open = to === '/issues' && issueCount !== undefined && issueCount > 0;
      const name = open ? `${label}, ${issueCount.toLocaleString('en-US')} open` : label;
      return (
        <ShortcutTooltip off={!collapsed} key={to} label={name} keys={keys}>
          <Link
            to={to}
            aria-label={name}
            className={RAIL_ITEM}
            activeProps={{ className: RAIL_ITEM_ACTIVE, 'aria-current': 'page' }}
          >
            <Icon size="lg" />
            {!collapsed && <span className="truncate">{label}</span>}
            {open && <RailBadge count={issueCount} tone="danger" collapsed={collapsed} />}
          </Link>
        </ShortcutTooltip>
      );
    })}

    <div className="mt-auto flex flex-col gap-1 pt-3">
      <NotificationsBell
        state={notifications}
        timeZone={timeZone}
        open={menu === 'notifications'}
        onOpenChange={toggle('notifications', onMenu)}
        collapsed={collapsed}
      />
      <ShortcutTooltip off={!collapsed} label="Settings" keys={SHELL_CHORDS.settings.keys}>
        <Link
          to="/settings"
          aria-label="Settings"
          className={RAIL_ITEM}
          activeProps={{ className: RAIL_ITEM_ACTIVE, 'aria-current': 'page' }}
        >
          <SettingsIcon size="lg" />
          {!collapsed && <span className="truncate">Settings</span>}
        </Link>
      </ShortcutTooltip>
      <UserMenu
        {...user}
        open={menu === 'account'}
        onOpenChange={toggle('account', onMenu)}
        collapsed={collapsed}
      />
      <ShortcutTooltip off={!collapsed} label="Expand sidebar" keys={SHELL_CHORDS.sidebar.keys}>
        <button
          type="button"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          onClick={onToggle}
          className={RAIL_ITEM}
        >
          {collapsed ? <SidebarExpandIcon size="lg" /> : <SidebarCollapseIcon size="lg" />}
          {!collapsed && <span className="truncate">Collapse</span>}
        </button>
      </ShortcutTooltip>
    </div>
  </nav>
);
